import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { getAuditTrailByid } from '../../../../../redux/auditTrailSlice'
import { getAllVersionHistories } from '../../../../../redux/versionHistorySlice'
import {
  getAllRichTextsForStep,
  getAllAttachmentsForStep,
  getAllFormsForStep,
  getAllVideosForStep,
  getAllCanvasesForStep,
  getAllSubstepsForStep,
} from '../../../../../redux/stepSlice'
import AppBar from '../../../../../common/AppBar'
import RevisionPreview from '../../MoveAction/RevisionPreview/RevisionPreview'
import PreviewProcedureRevision from './PreviewProcedureRevision'
import PreviewSection from './PreviewSection'
import PreviewStep from './PreviewStep'
import PreviewSubStep from './PreviewSubStep'
import PreviewAttachment from './PreviewAttachment'
import PreviewVideo from './PreviewVideo'
import PreviewRichText from './PreviewRichText'
import PreviewCanvas from './PreviewCanvas'
import Loader from '../../../../../common/Loader'
import {
  VersionHistoryPopup,
  getVersionHistoryAuditPreviewPath,
} from '../../../../../common/VersionHistoryPopup'
import VersionHistoryCopyReplace from '../../../../NodeActions/VersionHistoryCopy/VersionHistoryCopyReplace'
import ProcedureStatusInfo from '../../MoveAction/RevisionPreview/ProcedureStatusInfo'
import {
  resolveAuditTrailProcedureIdentity,
  enrichProcedureIdentityFromAuditSnapshots,
} from '../../../utils/auditTrailProcedureIdentity'
import { resolveProcedureIdsFromPartRevisionLabel } from '../../../utils/resolveProcedureIdsFromPartRevisionLabel'
import { dispatchAuditTrailNodeCopy } from '../../../utils/auditTrailDispatchNodeCopy'
import {
  auditTrailGeneralReviewPath,
  auditTrailNodeChangeReviewPath,
  isAuditTrailNodeChangeReviewEntry,
} from '../../utils/auditTrailNodeChangeReviewRoute'
import { resolveAuditTrailVersionId } from '../../../utils/auditTrailVersionIdResolve'

/** Form labels sometimes stored as section_name (e.g. "Section Name*") — not a real procedure title. */
function isJunkAuditBarTitle(value) {
  if (value == null || typeof value !== 'string') return false
  const t = value.trim()
  if (!t) return true
  if (/^section name\s*\*?$/i.test(t)) return true
  if (/^short description\s*\*?$/i.test(t)) return true
  if (/^description\s*\*?$/i.test(t)) return true
  if (/^name\s*\*?$/i.test(t) && t.length < 12) return true
  return false
}

function pickFirstProcedureBarLabel(...candidates) {
  for (const v of candidates) {
    if (v === undefined || v === null || v === '') continue
    if (typeof v === 'string') {
      const t = v.trim()
      if (!t || isJunkAuditBarTitle(t)) continue
      return t
    }
    return v
  }
  return null
}

/**
 * Merge nested audit `version_content` so `preview_tree_node_id` on the outer object is kept (same as RevisionPreview).
 */
function mergeAuditVcForBar(vc) {
  if (!vc || typeof vc !== 'object' || Array.isArray(vc)) return {}
  const inner = vc.version_content
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return { ...vc, ...inner }
  }
  return vc
}

/**
 * From snapshot JSON (S3): root has procedure tree_node_id + pageTitle (section-by-section save).
 * data[] rows: row.tree_node_id is the procedure tree id; detail.section_tree_node_id / section_id_ref are DB ids — do not use those for the bar id.
 * Child moves (attachment / rich-text / canvas / video): snapshot may omit root tree_node_id — fall back to audit preview_tree_node_id.
 */
function extractAppBarMetaFromSnapshotJson(json, vc) {
  if (!json || typeof json !== 'object') return null
  const v = mergeAuditVcForBar(vc || {})
  // Plain array snapshots (full procedure tree) have no root meta — use audit version_content
  if (Array.isArray(json) && json.length > 0) {
    const fromVc =
      v.procedure_tree_ref || v.tree_node_id || v.preview_tree_node_id
        ? {
            tree_node_id:
              v.procedure_tree_ref || v.tree_node_id || v.preview_tree_node_id,
            pageTitle:
              v.pageTitle ||
              v.elementTitle ||
              v.name ||
              null,
            breadcrumb_text: v.breadcrumb_text,
          }
        : null
    if (fromVc?.tree_node_id) return fromVc
  }
  const rootTreeId =
    json.tree_node_id ||
    json.procedure_tree_id ||
    json.section_tree_node_id ||
    v.preview_tree_node_id ||
    v.procedure_tree_ref ||
    v.tree_node_id
  if (rootTreeId) {
    const actionShow =
      (typeof json.action_to_show === 'string' && json.action_to_show.trim()) ||
      (typeof v.action === 'string' && v.action.trim()) ||
      ''
    const stepNumMatch = /step\s+(\d+)/i.exec(actionShow)
    const inferredStepTitle =
      json.type === 'step' && stepNumMatch ? `Step ${stepNumMatch[1]}` : null
    const substepLabelMatch = /substep\s+([\dA-Za-z]+)/i.exec(actionShow)
    const inferredSubstepTitle =
      json.type === 'substep' && substepLabelMatch
        ? `Substep ${substepLabelMatch[1]}`
        : null
    const attachmentNumMatch = /attachment\s+(\d+)/i.exec(actionShow)
    const inferredAttachmentTitle =
      json.type === 'attachment' && attachmentNumMatch
        ? `Attachment ${attachmentNumMatch[1]}`
        : null
    const canvasNumMatch = /canvas\s+(\d+)/i.exec(actionShow)
    const inferredCanvasTitle =
      json.type === 'canvas' && canvasNumMatch
        ? `Canvas ${canvasNumMatch[1]}`
        : null
    const richTextNumMatch = /rich\s+text\s+(\d+)/i.exec(actionShow)
    /* single_audit_trail envelopes often omit root type; still infer "Rich Text N" from action_to_show (e.g. RICH-TEXT_COPIED) */
    const inferredRichTextTitle =
      richTextNumMatch &&
      (json.type === 'rich-text' ||
        json.type === 'rich_text' ||
        json.type == null ||
        json.type === '')
        ? `Rich Text ${richTextNumMatch[1]}`
        : null
    const videoNumMatch = /video\s+(\d+)/i.exec(actionShow)
    /* Envelope may omit root type; infer "Video N" from action_to_show (e.g. VIDEO_COPIED, "User Copied Video 1 …") */
    const inferredVideoTitle =
      videoNumMatch &&
      (json.type === 'video' || json.type == null || json.type === '')
        ? `Video ${videoNumMatch[1]}`
        : null
    /* Moved child nodes: envelope may omit pageTitle; audit action_to_show is often e.g. VIDEO_MOVED / RICH_TEXT_MOVED */
    const auditAction = String(v.action_to_show || '')
    return {
      tree_node_id: rootTreeId,
      pageTitle:
        json.pageTitle ||
        inferredStepTitle ||
        inferredSubstepTitle ||
        inferredAttachmentTitle ||
        inferredCanvasTitle ||
        inferredRichTextTitle ||
        inferredVideoTitle ||
        v.pageTitle ||
        v.elementTitle ||
        inferredMovedChildBarLabel(json.type, auditAction),
      section_name: json.section_name,
      breadcrumb_text: json.breadcrumb_text,
    }
  }
  const targets = new Set(
    [
      v.section_tree_node_id,
      v.section_id_ref,
      v.step_tree_node_id,
      v.step_id_ref,
      v.sub_step_tree_node_id,
      v.substep_tree_node_id,
      v.tree_node_id,
      v.preview_tree_node_id,
      v.canvas_tree_node_id,
      v.canva_tree_node_id,
      v.rich_text_node_id,
      v.rich_text_tree_node_id,
      v.video_tree_node_id,
      v.attachment_tree_node_id,
    ].filter(Boolean),
  )
  const rows = Array.isArray(json.data) ? json.data : []
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const detail = row.detail || {}
    const rowCandidates = [
      row.tree_node_id,
      detail.section_tree_node_id,
      row.section_id_ref,
    ].filter(Boolean)
    const matchesTarget =
      targets.size > 0 && rowCandidates.some((id) => targets.has(id))
    if (matchesTarget && row.tree_node_id) {
      return {
        tree_node_id: row.tree_node_id,
        pageTitle: detail.section_name || json.pageTitle,
        section_name: detail.section_name || json.section_name,
        breadcrumb_text: json.breadcrumb_text,
      }
    }
  }
  return null
}

/** AppBar label for attachment / rich-text / canvas / video moves when snapshot omits pageTitle (single_audit_trail). */
function inferredMovedChildBarLabel(jsonType, auditAction) {
  const a = String(auditAction || '')
  if (!a) return null
  if (jsonType) {
    if (jsonType === 'attachment' && /ATTACHMENT/i.test(a)) return 'Attachment'
    if (
      (jsonType === 'canvas' || jsonType === 'canva') &&
      /(?:CANVAS|CANVA)/i.test(a)
    )
      return 'Canvas'
    if (
      (jsonType === 'rich-text' || jsonType === 'rich_text') &&
      /RICH[-_]?TEXT/i.test(a)
    )
      return 'Rich Text'
    if (jsonType === 'video' && /VIDEO/i.test(a)) return 'Video'
  }
  if (/ATTACHMENT/i.test(a)) return 'Attachment'
  if (/(?:CANVAS|CANVA)/i.test(a)) return 'Canvas'
  if (/RICH[-_]?TEXT/i.test(a)) return 'Rich Text'
  if (/VIDEO/i.test(a)) return 'Video'
  return null
}

const PreviewLayout = () => {
  const dispatch = useDispatch()
  const {audit_trail_id} = useParams()
  const navigate = useNavigate();
  const [revData, setRevData] = useState({version_content:{}})
  const [isLoadingChildren, setIsLoadingChildren] = useState(false)
  const [snapshotAppBarMeta, setSnapshotAppBarMeta] = useState(null)
  const [snapshotEnrichedIdentity, setSnapshotEnrichedIdentity] = useState(null)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [copyLoading, setCopyLoading] = useState(false)

  // Get copy/replace state from Redux
  const { isOpen: isCopyReplaceOpen } = useSelector(state => state.preview)
  const { versionHistories } = useSelector((state) => state.versionHistory)

  useEffect(()=>{
    (async()=>{
      const resp = await dispatch(getAuditTrailByid({id:audit_trail_id}))
      // Support both { results } and { data } response shapes
      const auditData = resp.payload?.results ?? resp.payload?.data ?? resp.payload;
      if (auditData && typeof auditData === 'object') setRevData(auditData);
      
      // Fetch children nodes if this is a step
      const action_to_show = auditData.version_content?.action_to_show;
      if (action_to_show?.includes("_STEP") && !action_to_show?.includes("_SUBSTEP")) {
        const stepId = auditData.version_content?.step_tree_node_id;
        const partId = auditData.version_content?.part_id_ref;
        const revisionId = auditData.version_content?.revision_id_ref;
        const sectionId = auditData.version_content?.section_id_ref;
        
        if (stepId && partId && revisionId && sectionId) {
          setIsLoadingChildren(true);
          try {
            // Fetch all children nodes for the step
            const [richTextsResp, attachmentsResp, formsResp, videosResp, canvasesResp, substepsResp] = await Promise.all([
              dispatch(getAllRichTextsForStep({ partId, revisionId, sectionId, stepId, substepId: null })),
              dispatch(getAllAttachmentsForStep({ partId, revisionId, sectionId, stepId, substepId: null })),
              dispatch(getAllFormsForStep({ partId, revisionId, sectionId, stepId, substepId: null })),
              dispatch(getAllVideosForStep({ partId, revisionId, sectionId, stepId, substepId: null })),
              dispatch(getAllCanvasesForStep({ partId, revisionId, sectionId, stepId, substepId: null })),
              dispatch(getAllSubstepsForStep({ partId, revisionId, sectionId, stepId })),
            ]);
            
            // Combine all children into a single array
            const richTexts = (richTextsResp.payload?.data || []).map(item => ({ ...item, type: 'richText' }));
            const attachments = (attachmentsResp.payload?.data || []).map(item => ({ ...item, type: 'attachment' }));
            const forms = (formsResp.payload?.data || []).map(item => ({ ...item, type: 'form' }));
            const videos = (videosResp.payload?.data || []).map(item => ({ ...item, type: 'video' }));
            const canvases = (canvasesResp.payload?.data || []).map(item => ({ ...item, type: 'canvas' }));
            const substeps = substepsResp.payload?.data || [];
            
            // For each substep, fetch its children
            const substepsWithChildren = await Promise.all(
              substeps.map(async (substep) => {
                const substepId = substep.sub_step_tree_node_id;
                if (substepId) {
                  const [subRichTexts, subAttachments, subForms, subVideos, subCanvases] = await Promise.all([
                    dispatch(getAllRichTextsForStep({ partId, revisionId, sectionId, stepId, substepId })),
                    dispatch(getAllAttachmentsForStep({ partId, revisionId, sectionId, stepId, substepId })),
                    dispatch(getAllFormsForStep({ partId, revisionId, sectionId, stepId, substepId })),
                    dispatch(getAllVideosForStep({ partId, revisionId, sectionId, stepId, substepId })),
                    dispatch(getAllCanvasesForStep({ partId, revisionId, sectionId, stepId, substepId })),
                  ]);
                  
                  const substepChildren = [
                    ...(subRichTexts.payload?.data || []).map(item => ({ ...item, type: 'richText' })),
                    ...(subAttachments.payload?.data || []).map(item => ({ ...item, type: 'attachment' })),
                    ...(subForms.payload?.data || []).map(item => ({ ...item, type: 'form' })),
                    ...(subVideos.payload?.data || []).map(item => ({ ...item, type: 'video' })),
                    ...(subCanvases.payload?.data || []).map(item => ({ ...item, type: 'canvas' })),
                  ];
                  
                  return {
                    ...substep,
                    type: 'substep',
                    children: substepChildren,
                  };
                }
                return { ...substep, type: 'substep', children: [] };
              })
            );
            
            // Combine all children
            const allChildren = [
              ...richTexts,
              ...attachments,
              ...forms,
              ...videos,
              ...canvases,
              ...substepsWithChildren,
            ];
            
            // Update revData with children
            setRevData(prev => ({
              ...prev,
              version_content: {
                ...prev.version_content,
                children: allChildren,
                substeps: substepsWithChildren,
              }
            }));
          } catch (error) {
            console.error('Error fetching children nodes:', error);
          } finally {
            setIsLoadingChildren(false);
          }
        }
      }
    })()
  },[dispatch,audit_trail_id])

  useEffect(() => {
    let cancelled = false
    setSnapshotEnrichedIdentity(null)
    const hasUrl = revData?.new_content_snapshot || revData?.previous_content_snapshot
    if (!hasUrl) return () => { cancelled = true }
    ;(async () => {
      const base = resolveAuditTrailProcedureIdentity(revData)
      const enriched = await enrichProcedureIdentityFromAuditSnapshots(revData, base)
      if (!cancelled) setSnapshotEnrichedIdentity(enriched)
    })()
    return () => { cancelled = true }
  }, [audit_trail_id, revData?.new_content_snapshot, revData?.previous_content_snapshot])

  useEffect(() => {
    setSnapshotAppBarMeta(null)
    const url = revData?.new_content_snapshot
    if (!url || typeof url !== 'string') return
    const vc = revData?.version_content || {}
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(url)
        if (!res.ok || cancelled) return
        const json = await res.json()
        if (cancelled) return
        const meta = extractAppBarMetaFromSnapshotJson(json, {
          ...vc,
          action: revData?.action,
        })
        setSnapshotAppBarMeta(meta)
      } catch {
        if (!cancelled) setSnapshotAppBarMeta(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [revData?.new_content_snapshot, audit_trail_id])
  const action_to_show = revData.version_content?.action_to_show;

  // Determine if this is a move/copy action that needs full revision preview
  const isMoveOrCopyAction = action_to_show?.includes('_MOVED') || action_to_show?.includes('_COPIED');
  // Use RevisionPreview when snapshot URLs exist (e.g. CREATE_STEP) so View Original/View New shows proper data
  const hasSnapshotUrls = !!(revData.previous_content_snapshot && revData.new_content_snapshot);
  const useRevisionPreview = isMoveOrCopyAction || hasSnapshotUrls;
  const hasNewSnapshot = !!revData?.new_content_snapshot;

  // Header values for when we render node preview (no RevisionPreview) - e.g. CREATE_VIDEO with no snapshot URLs
  const vc = revData.version_content || {};
  const resolvedAuditVersionId = resolveAuditTrailVersionId(revData)

  const handleTablesVersionHistory = useCallback(async () => {
    const vid = resolveAuditTrailVersionId(revData)
    if (!vid) return
    await dispatch(getAllVersionHistories({ versionId: vid }))
    setShowVersionHistory(true)
  }, [dispatch, revData])

  const data = { ...vc, vrsn_id: resolvedAuditVersionId };
  const displayStatus =
    revData.parent_version_data?.version_content?.status ??
    vc.status ??
    vc.status_changed_to ??
    revData.procedure_revision?.status;
  const displayRevision =
    revData.parent_version_data?.version_content?.rev ??
    revData.parent_version_data?.version_content?.revision ??
    vc.rev ??
    vc.revision ??
    vc.part_rev ??
    revData.procedure_revision?.revision ??
    revData.procedure_revision?.rev ??
    revData.rev ??
    revData.revision;

  const titleSource =
    revData.parent_version_data?.version_content ||
    revData.version_content?.version_content ||
    revData.version_content;

  /**
   * Procedure tree id for AppBar: prefer new_content_snapshot root/row tree_node_id.
   * Do not prefer section_tree_node_id / section_id_ref from get-by-id — those are often DB section ids, not procedure tree_node_id.
   */
  const treeNodeIdForBar =
    snapshotAppBarMeta?.tree_node_id ||
    vc.tree_node_id ||
    vc.procedure_tree_ref ||
    vc.step_tree_node_id ||
    vc.sub_step_tree_node_id ||
    vc.substep_tree_node_id ||
    vc.video_tree_node_id ||
    vc.canvas_tree_node_id ||
    vc.canva_tree_node_id ||
    vc.rich_text_node_id ||
    vc.rich_text_tree_node_id ||
    vc.attachment_tree_node_id ||
    vc.preview_tree_node_id ||
    vc.version_id_ref ||
    revData.version_id ||
    vc.section_tree_node_id ||
    vc.section_id_ref ||
    null;

  const nodeLabelForBar =
    pickFirstProcedureBarLabel(
      snapshotAppBarMeta?.pageTitle,
      snapshotAppBarMeta?.section_name,
      typeof snapshotAppBarMeta?.breadcrumb_text === 'string'
        ? snapshotAppBarMeta.breadcrumb_text.split(' > ').pop()
        : null,
      vc.pageTitle,
      titleSource?.pageTitle,
      titleSource?.elementTitle,
      titleSource?.name,
      vc.section_name,
      titleSource?.section_name,
      typeof titleSource?.breadcrumb_text === 'string'
        ? titleSource.breadcrumb_text.split(' > ').pop()
        : null,
      typeof vc.breadcrumb_text === 'string'
        ? vc.breadcrumb_text.split(' > ').pop()
        : null,
      titleSource?.stepTitle,
      titleSource?.section,
    ) || 'Item'

  const procedureIdentityForUI =
    snapshotEnrichedIdentity ?? resolveAuditTrailProcedureIdentity(revData)

  // Build revision-submission params from an audit object (revData or fresh fetch)
  const getCancelParams = (audit, identityOverride) => {
    const id = identityOverride ?? resolveAuditTrailProcedureIdentity(audit);
    return {
      partId: id.partId,
      revision: id.revision || undefined,
      partNumber: id.partNumber || undefined,
      revisionId: id.revisionId,
    };
  };

  /**
   * Prefer procedure / leaf tree_node_id from snapshot (e.g. Save Canvas envelope root) so the bar
   * matches S3 JSON; fall back to audit version_id when snapshot meta is not loaded yet.
   */
  // NOTE: AppBar "Version" must reflect audit version UUID (same source as Tables → Version History).
  const versionIdShown = String(resolvedAuditVersionId || "");

  const displayTitle = resolvedAuditVersionId
      ? `${nodeLabelForBar} (Version ${versionIdShown}) Selected`
      : "Loading…";

  const handleReviewClick = () => {
    const path = isAuditTrailNodeChangeReviewEntry(revData)
      ? auditTrailNodeChangeReviewPath(audit_trail_id)
      : auditTrailGeneralReviewPath(audit_trail_id)
    navigate(path)
  }

  const handleViewAuditTrail = () => {
    navigate(`/audit-trail/${audit_trail_id}`)
  }

  // Navigate to revision-submission on Cancel (same pattern as NodeAppBar: .../type/partNumber/.../redirect)
  const handleCancel = async () => {
    const new_content_snapshot_url = revData.new_content_snapshot
    try {
      const response = await fetch(new_content_snapshot_url);
      if (!response.ok) {
        throw new Error(`Failed to fetch snapshot: ${response.statusText}`);
      }

      const data = await response.json();
      const part_ref = data?.data[0]?.part_ref
      const procedure_rev_ref = data?.data[0]?.procedure_rev_ref

      navigate(
        `/revision-submission/${part_ref}/${data?.revision}/${data?.item_code_type}/${data?.partNumber}/${procedure_rev_ref}/redirect`,
        {
          state:{
            fromAuditTrail: true,
            auditTrailBackId: audit_trail_id,
            auditTrailSavedAtIso: revData?.audit_created_at,
            cancel_screen_data:data
          }
        }
      )

    } catch (error) {
      console.error("Error fetching new_content_snapshot:", error);
    }
  };

  const handleCopy = useCallback(async () => {
    if (copyLoading) return
    setCopyLoading(true)
    try {
      // Extract breadcrumb from multiple sources including short_description HTML content
      let breadcrumbText = snapshotAppBarMeta?.breadcrumb_text || titleSource?.breadcrumb_text || vc.breadcrumb_text;
      
      // If breadcrumbText is still undefined, try to extract it from short_description HTML content
      if (!breadcrumbText) {
        // First try vc.short_description (for some cases where it exists)
        if (vc?.short_description) {
          const htmlMatch = vc.short_description.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
          if (htmlMatch) {
            breadcrumbText = htmlMatch[0].replace(/&gt;/g, '>');
          } else {
          }
        }
        
        // Check if new_content_snapshot is a URL (string) - need to fetch the data
        if (!breadcrumbText && typeof revData?.new_content_snapshot === 'string' && revData.new_content_snapshot.endsWith('.json')) {
          try {
            // For now, let's try to extract from version_content as a fallback
            // The full external fetching would require async handling which is complex in this context
          } catch (error) {
          }
        }
        
        // Try new_content_snapshot.detail.short_description (if it's an object, not a URL)
        if (!breadcrumbText && revData?.new_content_snapshot?.detail?.short_description) {
          const snapshotHtmlMatch = revData.new_content_snapshot.detail.short_description.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
          if (snapshotHtmlMatch) {
            breadcrumbText = snapshotHtmlMatch[0].replace(/&gt;/g, '>');
          } else {
          }
        }
        
        // If still not found, try detail.short_description (like RevisionPreview.js does)
        if (!breadcrumbText && revData?.detail?.short_description) {
          const detailHtmlMatch = revData.detail.short_description.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
          if (detailHtmlMatch) {
            breadcrumbText = detailHtmlMatch[0].replace(/&gt;/g, '>');
          } else {
          }
        }
        
        // Try to extract from version_content as a last resort
        if (!breadcrumbText && vc) {
          // Look for breadcrumb in any string property of vc
          const vcString = JSON.stringify(vc);
          const vcHtmlMatch = vcString.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
          if (vcHtmlMatch) {
            breadcrumbText = vcHtmlMatch[0].replace(/&gt;/g, '>');
          } else {
          }
        }
        
        // Try to extract breadcrumb from the entire revData structure
        if (!breadcrumbText && revData) {
          const revDataString = JSON.stringify(revData);
          const revDataHtmlMatch = revDataString.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
          if (revDataHtmlMatch) {
            breadcrumbText = revDataHtmlMatch[0].replace(/&gt;/g, '>');
          } else {
          }
        }
        
        // Try to extract from previous_content_snapshot if available
        if (!breadcrumbText && revData?.previous_content_snapshot && typeof revData.previous_content_snapshot === 'object') {
          const prevSnapshotString = JSON.stringify(revData.previous_content_snapshot);
          const prevSnapshotMatch = prevSnapshotString.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
          if (prevSnapshotMatch) {
            breadcrumbText = prevSnapshotMatch[0].replace(/&gt;/g, '>');
          } else {
          }
        }
        
        // Try to build breadcrumb from section/step/substep IDs if HTML extraction failed
        if (!breadcrumbText && revData && revData.new_content_snapshot && typeof revData.new_content_snapshot === 'object') {
          // Try to find video node in the new_content_snapshot data structure
          const newContent = revData.new_content_snapshot;
          // Look for video nodes with section/step/substep IDs
          if (newContent.data && Array.isArray(newContent.data)) {
            for (let sectionIndex = 0; sectionIndex < newContent.data.length; sectionIndex++) {
              const section = newContent.data[sectionIndex];
              if (section.type === 'section' && section.steps) {
                for (let stepIndex = 0; stepIndex < section.steps.length; stepIndex++) {
                  const step = section.steps[stepIndex];
                  if (step.type === 'step' && step.substeps) {
                    for (let substepIndex = 0; substepIndex < step.substeps.length; substepIndex++) {
                      const substep = step.substeps[substepIndex];
                      if (substep.type === 'substep' && substep.children) {
                        for (let childIndex = 0; childIndex < substep.children.length; childIndex++) {
                          const child = substep.children[childIndex];
                          if (child.type === 'video' && child.section_id_ref && child.step_id_ref) {
                            const sectionId = child.section_id_ref;
                            const stepId = child.step_id_ref;
                            const substepId = child.substep_id_ref;
                            
                            // Build breadcrumb from array indices
                            const sectionNumber = sectionIndex + 1; // Sections are 1-indexed
                            const stepNumber = stepIndex + 1; // Steps are 1-indexed
                            const substepLetter = substepIndex >= 0 ? String.fromCharCode(65 + substepIndex) : null; // A=0, B=1, etc.
                            
                            // Build breadcrumb from extracted numbers
                            if (sectionNumber && stepNumber) {
                              breadcrumbText = `Section ${sectionNumber} > Step ${stepNumber}`;
                              if (substepLetter) {
                                breadcrumbText += ` > Substep ${stepNumber}${substepLetter}`;
                              }
                              // Store globally for other components to use
                              window.lastExtractedBreadcrumb = breadcrumbText;
                              window.lastExtractedBreadcrumbData = {
                                breadcrumb: breadcrumbText,
                                videoNode: child
                              };
                              break; // Found what we need, exit loops
                            }
                          }
                        }
                        if (breadcrumbText) break; // Found breadcrumb, exit loops
                      }
                    }
                    if (breadcrumbText) break; // Found breadcrumb, exit loops
                  }
                }
                if (breadcrumbText) break; // Found breadcrumb, exit loops
              }
            }
          }
        }
        
        // Try to use globally stored breadcrumb from RevisionPreview as last resort
        if (!breadcrumbText && window.lastExtractedBreadcrumb) {
          breadcrumbText = window.lastExtractedBreadcrumb;
        }
        
      } else {
      }
      await dispatchAuditTrailNodeCopy({
        dispatch,
        auditTrailId: audit_trail_id,
        revData,
        nodeTitleOverride: nodeLabelForBar,
        resolved: null,
        snapshotEnrichedIdentity,
        breadcrumbText,
      })
    } finally {
      setCopyLoading(false)
    }
  }, [
    audit_trail_id,
    copyLoading,
    dispatch,
    nodeLabelForBar,
    revData,
    snapshotEnrichedIdentity,
    snapshotAppBarMeta,
    titleSource,
    vc,
  ])
  
  if (isLoadingChildren) {
    return <Loader text="Loading step data with children..." />;
  }
  
  return (
    <>
      <AppBar
        title={displayTitle}
        titleFontSize="18px"
        showTablesDropdown={true}
        versionHistoryHandler={
          resolvedAuditVersionId ? handleTablesVersionHistory : null
        }
        showCancelButton={true}
        onCancel={handleCancel}
        showBackButton={false}
        showProfileButton={false}
        buttons={[
          {
            label: "View Audit Trail Entry",
            onClick: handleViewAuditTrail,
          },
          {
            label: "Review",
            onClick: handleReviewClick,
          },
          {
            label: "Copy",
            onClick: handleCopy,
            loading: copyLoading,
          }
        ]}
      />

      {/* For move/copy or when snapshot URLs exist (e.g. CREATE_STEP), show full revision preview with View Original/View New */}
      {useRevisionPreview ? (
        <RevisionPreview
          hideAppBar={true}
          externalAuditTrailData={revData}
          procedureAppBarTitleBase={nodeLabelForBar}
        />
      ) : !hasNewSnapshot ? (
        <>
          <ProcedureStatusInfo
            item_code_type={vc.item_code_type ?? revData.procedure_revision?.item_code_type}
            partNumber={vc.partNumber ?? revData.procedure_revision?.part_number ?? revData.procedure_revision?.partNumber ?? revData.partNumber}
            status={displayStatus}
            revision={displayRevision}
            partDescription={vc.partDescription ?? vc.short_description ?? vc.description ?? revData.procedure_revision?.description}
            showDisabledCheckboxes={true}
            showProcedureHeaderCheckbox={false}
            whenSaved={revData.audit_date}
          />
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#666",
              fontSize: "16px",
            }}
          >
            No Data
          </div>
        </>
      ) : (
        /* For edit/update/create actions, show individual node preview with procedure header */
        <>
          <ProcedureStatusInfo
            item_code_type={vc.item_code_type ?? revData.procedure_revision?.item_code_type}
            partNumber={vc.partNumber ?? revData.procedure_revision?.part_number ?? revData.procedure_revision?.partNumber ?? revData.partNumber}
            status={displayStatus}
            revision={displayRevision}
            partDescription={vc.partDescription ?? vc.short_description ?? vc.description ?? revData.procedure_revision?.description}
            showDisabledCheckboxes={true}
            showProcedureHeaderCheckbox={false}
            whenSaved={revData.audit_date}
          />
          {/* {
            action_to_show === "UPDATE_PROCEDURE_REVISION" || action_to_show === "CREATE_PROCEDURE_REVISION"
            ?  <PreviewProcedureRevision version_content={data}/> :
            action_to_show?.includes("_SECTION") 
            ? <PreviewSection version_content={data}/> :  
            action_to_show?.includes("_STEP") 
            ? <PreviewStep version_content={data}/> : 
            action_to_show?.includes("_SUBSTEP")
            ? <PreviewSubStep version_content={data}/> : 
            data.type?.includes("attachment")
            ? <PreviewAttachment version_content={data}/> : 
            action_to_show?.includes("_CANVAS") 
            ? <PreviewCanvas version_content={data}/> :
            (action_to_show?.includes("_VIDEO") || action_to_show?.includes("VIDEO_"))
            ? <PreviewVideo version_content={data}/> :
            (action_to_show?.includes("_RICH_TEXT") || action_to_show?.includes("RICH-TEXT_"))
            ? <PreviewRichText version_content={data}/> : ""
          } */}
        </>
      )}
      
      {/* Version History Copy/Replace Component */}
      {isCopyReplaceOpen && (
        <VersionHistoryCopyReplace 
          auditTrailData={revData}
          auditTrailId={audit_trail_id}
          breadcrumbText={(() => {
        let text = snapshotAppBarMeta?.breadcrumb_text || titleSource?.breadcrumb_text || vc.breadcrumb_text;
        
        // If breadcrumbText is still undefined, try to extract it from short_description HTML content
        if (!text && vc?.short_description) {
          const htmlMatch = vc.short_description.match(/(?:Section|Step|Substep)\s+\d+(?:\s*&gt;\s*(?:Section|Step|Substep)\s+\d+)*(?:\s*&gt;\s*Video\s+\d+)?/i);
          if (htmlMatch) {
            text = htmlMatch[0].replace(/&gt;/g, '>');
          }
        }
        
        // If still no text, try to use globally stored breadcrumb from RevisionPreview
        if (!text && window.lastExtractedBreadcrumb) {
          text = window.lastExtractedBreadcrumb;
        }
        
        return text;
      })()}
        />
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          versionHistories={versionHistories || []}
          mode="edit"
          versionId={resolvedAuditVersionId}
          nodeId={
            revData.version_content?.element_id ||
            revData.version_content?.step_tree_node_id
          }
          nodeIdField="element_id"
          breadcrumbText={revData.version_content?.breadcrumb_text}
          procedureRevisionIdForNodeHistory={
            revData.version_content?.procedure_revision_id
          }
          restrictNodeHistoryToProcedureRevision={
            !!revData.version_content?.procedure_revision_id
          }
          initialFilterType="versions_for_this_page"
          currentVersionId={resolvedAuditVersionId}
          emptyMessage="No version history available"
          onViewDetails={(row) => {
            const aid = row.audit_trail_id || row.audit_trail_id_ref
            if (!aid) return
            const path = getVersionHistoryAuditPreviewPath(
              aid,
              row.action || row.action_to_show,
            )
            if (!path) return
            setShowVersionHistory(false)
            navigate(path)
          }}
          isRowViewActionDisabled={(row) =>
            !(row.audit_trail_id || row.audit_trail_id_ref)
          }
        />
      )}
    </>
  )
}

export default PreviewLayout